import { type ReactNode, useRef } from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeDisabled?: boolean;
  /** Tailwind max-width override, e.g. "sm:max-w-xl". Defaults to sm:max-w-lg. */
  widthClassName?: string;
}

/**
 * Right-side slide-over for inspecting / acting on a single list row. The
 * standard detail surface across Fulfillment screens (replaces centered
 * modals). Header is sticky; body scrolls.
 */
export function DetailDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closeDisabled = false,
  widthClassName = "sm:max-w-lg",
}: DetailDrawerProps) {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && closeDisabled) return;
        onOpenChange(nextOpen);
      }}
    >
      <SheetContent
        side="right"
        closeDisabled={closeDisabled}
        className={cn("flex w-full flex-col gap-0 p-0", widthClassName)}
        onOpenAutoFocus={() => {
          const activeElement = document.activeElement;
          returnFocusRef.current =
            activeElement instanceof HTMLElement ? activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          const returnTarget = returnFocusRef.current;
          returnFocusRef.current = null;
          if (!returnTarget?.isConnected) return;
          event.preventDefault();
          returnTarget.focus();
        }}
        // Radix warns when a dialog/sheet has no description. When a caller
        // omits one, explicitly opt out instead of emitting a console warning.
        {...(description ? {} : { "aria-describedby": undefined })}
      >
        {/* pr-12 keeps the title clear of the built-in close button (right-4). */}
        <SheetHeader className="border-b px-6 py-4 pr-12 text-left">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="shrink-0 border-t bg-background px-6 py-4">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
